import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { CHANNEL_ICONS, GUARD_CHANNELS, MAX_BOT_REPLY_ALLOWANCE } from '@/lib/automation-guards';
import { InfoTip, SettingsSelect } from '@/modules/settings';
import type { AutomationGuards, GuardChannel } from '@/types';

/** 'default' = no allowance saved: Kim stops at the first message from Kim or an agent. */
const ALLOWANCE_OPTIONS = [
	{ value: 'default', label: 'Default (first)' },
	...Array.from({ length: MAX_BOT_REPLY_ALLOWANCE + 1 }, (_, n) => ({ value: String(n), label: `${n} ${n === 1 ? 'message' : 'messages'}` }))
];

const COLUMN_CLASS = 'flex h-full shrink-0 items-center border-l border-gray-300 pl-3';

interface HandoffByChannelProps {
	guards: AutomationGuards;
	onChange: (patch: Partial<AutomationGuards>) => void;
	disabled?: boolean;
}

/** Per channel: does an agent's reply stop Kim (saved inverted), and after how many messages Kim stops. */
export function HandoffByChannel({ guards, onChange, disabled }: HandoffByChannelProps) {
	const stopsOnAgentReply = (channel: GuardChannel) => !guards.humanReplyExemptChannels.includes(channel);

	const setStopsOnAgentReply = (channel: GuardChannel, stops: boolean) =>
		onChange({
			humanReplyExemptChannels: stops
				? guards.humanReplyExemptChannels.filter((c) => c !== channel)
				: [...guards.humanReplyExemptChannels, channel]
		});

	const setAllowance = (channel: GuardChannel, raw: string) => {
		const next = { ...guards.botReplyAllowance };
		if (raw === 'default') delete next[channel];
		else next[channel] = Number(raw);
		onChange({ botReplyAllowance: next });
	};

	return (
		<div className='flex flex-col gap-4'>
			<div role='table' aria-label='Handoff by channel' className='overflow-hidden rounded-[12px] border border-gray-300 bg-white'>
				<div role='row' className='flex items-center gap-3 bg-gray-100 px-4 text-[12px] font-medium leading-4 text-gray-900'>
					<span role='columnheader' className='flex-1 py-2'>
						Channel
					</span>
					<span role='columnheader' className={cn(COLUMN_CLASS, 'w-[170px] gap-1')}>
						Stop after an agent replies
						<InfoTip label='About stopping after an agent replies'>
							If one of your agents has already replied, Kim only drafts. Kim&apos;s own replies count as an agent&apos;s when your
							helpdesk posts them from a shared login, so set a cap for any channel you turn this on for.
						</InfoTip>
					</span>
					<span role='columnheader' className={cn(COLUMN_CLASS, 'w-[172px] gap-1')}>
						Kim stops after
						<InfoTip label='About when Kim stops'>
							The most messages a ticket may have from Kim or your agents before Kim stops. Stops loops with out-of-office senders.
							Start chat at 1; a full chat resolution usually needs 4 to 5.
						</InfoTip>
					</span>
				</div>
				{GUARD_CHANNELS.map(({ id, label }) => {
					const Icon = CHANNEL_ICONS[id];
					return (
						<div key={id} role='row' className='flex h-14 items-center gap-3 border-t border-gray-300 px-4 py-2'>
							<span role='cell' className='flex flex-1 items-center gap-2 text-[13px] font-medium leading-[18px] text-ink'>
								<Icon className='size-4 text-gray-900' />
								{label}
							</span>
							<span role='cell' className={cn(COLUMN_CLASS, 'w-[170px] justify-center pl-3')}>
								<Checkbox
									checked={stopsOnAgentReply(id)}
									disabled={disabled}
									aria-label={`Stop after an agent replies on ${label}`}
									onCheckedChange={(checked) => setStopsOnAgentReply(id, checked === true)}
								/>
							</span>
							<span role='cell' className={cn(COLUMN_CLASS, 'w-[172px]')}>
								<SettingsSelect
									value={guards.botReplyAllowance[id] === undefined ? 'default' : String(guards.botReplyAllowance[id])}
									onValueChange={(value) => setAllowance(id, value)}
									options={ALLOWANCE_OPTIONS}
									disabled={disabled}
									aria-label={`Kim stops after, on ${label}`}
								/>
							</span>
						</div>
					);
				})}
			</div>
			<p className='text-[12px] leading-4 text-gray-900'>
				Kim stops after counts every message on the ticket from Kim or your agents. The default stops Kim at the first one.
			</p>
		</div>
	);
}
